<script setup lang="ts">
  import { useElementOption } from '@/composable/elementOptions'
  import { useDocument } from '@/composition'
  import { useBuilderStore } from '@/store/builder'
  import {
    CustomLineHeight,
    ELEMENTS_META,
    ExtendedFontFamily,
    ExtendedHeading,
    extractFontFamiliesFromJSON,
    FontFamilyV2,
    IElement,
    InsertGapOnPaste,
    MagicPlaceholderExtension,
    Storage,
  } from '@gohighlevel/ghl-proposals-common'
  import {
    Color,
    LineHeight,
    Link,
    RequiredExtensions,
    TextAlign,
    TextBlocks,
    TextSize,
    UIRichTextEditor,
  } from '@gohighlevel/ghl-text-editor'
  import BulletList from '@tiptap/extension-bullet-list'
  import HardBreak from '@tiptap/extension-hard-break'
  import ListItem from '@tiptap/extension-list-item'
  import TextStyle from '@tiptap/extension-text-style'
  import Underline from '@tiptap/extension-underline'
  import StarterKit from '@tiptap/starter-kit'
  import { Editor, EditorState } from '@tiptap/vue-3'
  import { computed, defineExpose, nextTick, onMounted, onUnmounted, PropType, provide, ref, watch } from 'vue'

  const editor = ref<Editor>()
  const store = useBuilderStore()
  const { updateOption } = useElementOption()
  const { loadingCustomValues, documentVersion } = useDocument()

  // Add a ref to store link state
  const currentLinkState = ref<any>(null)

  // Provide the link state to child components
  provide('currentLinkState', currentLinkState)

  // Add a function to update link state
  const updateLinkState = () => {
    if (editor.value) {
      const attrs = editor.value.getAttributes('link')
      if (attrs?.href) {
        currentLinkState.value = attrs
        // Provide the link state to child components
        provide('rte-link-state', attrs)
      }
    }
  }

  const props = defineProps({
    genKey: { type: String },
    element: {
      type: Object as PropType<IElement>,
    },
    customValues: {
      type: Object as PropType<Record<string, string>>,
      required: true,
    },
  })
  const isEditable = computed(() => store.isDocumentEditable)

  const CustomBulletList = BulletList.extend({
    addCommands() {
      return {
        ...this.parent?.(),
        toggleCustomBulletList: () => editor => {
          return editor.commands.toggleBulletList()
        },
      }
    },
  })

  const HardBreakExtension = HardBreak.extend({
    addKeyboardShortcuts() {
      return {
        Enter: () => {
          if (this.editor.isActive('orderedList') || this.editor.isActive('bulletList')) {
            return this.editor.chain().createParagraphNear().run()
          }
          return this.editor.commands.setHardBreak()
        },
      }
    },
  })

  editor.value = new Editor({
    content: props.element?.component.options.text,
    extensions: [
      StarterKit.configure({
        paragraph: {
          HTMLAttributes: {
            class: 'break-inside-auto',
          },
        },
      }),
      MagicPlaceholderExtension.configure({
        placeholderValues: props.customValues,
        loading: loadingCustomValues.value,
      }),
      TextSize,
      RequiredExtensions,
      TextStyle,
      ...((documentVersion.value ?? 1) === 1 ? [ExtendedFontFamily] : []),
      ...((documentVersion.value ?? 1) > 1 ? [FontFamilyV2] : []),
      TextBlocks,
      ...((documentVersion.value ?? 1) > 1 ? [ExtendedHeading] : []), // don't change the order, this should be added after TextBlocks
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        protocols: ['ftp', 'mailto', '#popup', 'sms', 'tel', '{{', '/', 'http', 'https'],
        autolink: true,
        linkOnPaste: false,
      }),
      // Replace the LineHeight extension with CustomLineHeight
      ...((documentVersion.value ?? 1) > 1
        ? [
            CustomLineHeight.configure({
              defaultLineHeight: 1.5,
              types: ['paragraph', 'heading', 'blockquote', 'listItem'],
            }),
          ]
        : []),
      ...((documentVersion.value ?? 1) === 1 ? [LineHeight] : []),
      ListItem,
      CustomBulletList,
      ...((documentVersion.value ?? 1) === 1
        ? [
            HardBreakExtension.configure({
              HTMLAttributes: {
                class: 'break-after-auto page-break',
              },
            }),
          ]
        : []),
      Storage.configure({
        storage: {
          version: props.element?.version ?? 1,
          documentVersion: documentVersion.value ?? 1,
        },
      }),
      ...((documentVersion.value ?? 1) > 1 ? [InsertGapOnPaste] : []),
    ],
    autofocus: false,
    editable: isEditable.value,
    onUpdate({ editor }) {
      updateOption(ELEMENTS_META.TEXT, editor.getHTML(), props?.element?.id, props?.element?.type)
    },
    editorProps: {
      handleClick: () => {
        updateLinkState()
        return false // Let other click handlers run
      },
      attributes: (state: EditorState) => {
        return {
          ...state.doc.attrs,
          ...(documentVersion.value === 1 ? { style: 'font-family: sans-serif' } : {}),
          ...(documentVersion.value > 1 ? { style: 'font-family: Open Sans' } : {}),
        }
      },
    },
    parseOptions: {
      preserveWhitespace: true,
    },
  })

  function onRichTextEditorFocus() {
    store.updateEditorInstance(editor.value as unknown as typeof Editor)
    updateLinkState()
  }

  function onRichTextEditorSelectionUpdate() {
    updateLinkState()
  }

  function onRichTextEditorBlur() {
    if (documentVersion.value > 1) {
      const fonts = extractFontFamiliesFromJSON(editor.value?.getJSON())
      store.setLoadedFontsFromEditor(Array.from(fonts))
    }
    if (props.element?.id) {
      store.flushPendingSectionPropagations(props.element.id)
    }
  }

  function onRichTextEditorCreate() {
    if (documentVersion.value > 1) {
      const fonts = extractFontFamiliesFromJSON(editor.value?.getJSON())
      store.setLoadedFontsFromEditor(Array.from(fonts))
    }
    if (store.activeElementId === props.element?.id) {
      nextTick(() => {
        store.updateEditorInstance(editor.value as unknown as typeof Editor)
      })
    }
  }

  // Export currentLinkState for use in the modal
  defineExpose({
    currentLinkState,
  })

  watch(isEditable, () => {
    editor.value?.setEditable(isEditable.value)
  })

  // Tiptap doesn't react to its `content` prop after construction, so when the store mutates
  // `element.component.options.text` (cross-page mirror update, undo/redo, or any other write),
  // we have to explicitly push the new text into the editor. Watching the value directly means
  // Vue's reactivity does the fan-out for us — every editor on every page that holds the same
  // logical element receives its own watch fire when its prop's text changes. `flush: 'post'`
  // ensures the watcher runs AFTER Vue has patched the DOM with the latest cloned element so
  // `props.element.component.options.text` reflects the most recent value (default `pre` would
  // run with the previous value and cause a one-step lag on non-host pages).
  //
  // Guard with `isFocused` instead of an HTML-string compare. The previous `getHTML() !== newText`
  // optimisation was meant to skip the no-op re-parse on the host editor (which just emitted
  // this same value via onUpdate → mirror), but the comparison is unreliable for heading-level
  // attributes: tiptap's serialise/parse round-trip for a heading combines contributions from
  // `ExtendedHeading.style` (font-size, line-height) and `TextAlign.textAlign` (text-align),
  // and the resulting style-attribute ordering / spacing is not always byte-identical to the
  // input. That meant non-host editors could find `getHTML() === newText` and skip setContent
  // even though their rendered DOM still reflected the OLD text-align. `isFocused` is a precise
  // discriminator: the host editor (where the user is typing) is focused; every other replica
  // isn't. Skip the focused one (don't disturb the cursor) and always re-apply setContent on
  // the others so alignment, color and any other inline style on a heading / paragraph
  // round-trips correctly across pages.
  watch(
    () => props.element?.component?.options?.text,
    newText => {
      if (newText == null || !editor.value) return
      if (editor.value.isFocused) return
      // Self-stomp guard. The `isFocused` guard above already catches the host editor
      // while the user is actively typing — but it FAILS in one specific window:
      // clicking a toolbar dropdown (font, size, etc.) blurs the editor for the duration
      // of the dropdown interaction, then the command applies the chosen value, fires
      // `onUpdate`, the store mutates `element.component.options.text`, and this watcher
      // post-flushes — all while `isFocused` is briefly `false`. Without this check, the
      // watcher then calls `setContent` with the editor's OWN just-emitted HTML, which
      // resets the cursor (Tiptap doesn't preserve selection across `setContent`), and
      // the browser scroll-into-views the new cursor — manifesting as a "page jumps
      // down ~1 page" symptom on large text blocks. Comparing `getHTML()` to `newText`
      // detects this self-stomp: the editor already has the new content, so there's
      // nothing to apply — no setContent, no cursor reset, no scroll. The H/F replica
      // case is unaffected because replica editors have DIFFERENT HTML from `newText`
      // (they haven't applied the mirror yet), so they fall through to setContent.
      if (editor.value.getHTML() === newText) return
      // `preserveWhitespace: 'full'` keeps leading/inner whitespace that the default
      // parser collapses — important for headings whose visual centering came from
      // leading spaces rather than `text-align`, which the host editor preserves
      // because the user typed them in directly (no parse round-trip) while
      // replicas hit this setContent path and would otherwise lose the spaces.
      // It is also a no-op for text-align since the style attribute is browser-parsed
      // independently of whitespace settings — safe to always pass.
      editor.value.commands.setContent(newText, false, { preserveWhitespace: 'full' })
    },
    { flush: 'post' }
  )

  watch(
    () => store.moveElementDragState,
    val => {
      editor.value?.setEditable(!val)
    }
  )

  // Helper function to check if an element is at least partially visible in the viewport
  const isElementInViewport = (element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect()
    const windowHeight = window.innerHeight || document.documentElement.clientHeight
    const windowWidth = window.innerWidth || document.documentElement.clientWidth
    // Check if element intersects with viewport (at least partially visible)
    return rect.bottom > 0 && rect.right > 0 && rect.top < windowHeight && rect.left < windowWidth
  }

  // Watch for when this element becomes active and set the editor instance
  // This ensures the editor instance is set when clicking on a text block
  watch(
    () => store.activeElementId,
    newActiveElementId => {
      // If this element becomes active and editor exists, set the editor instance directly
      // This ensures the toolbar (font, line height, etc.) is shown immediately
      if (newActiveElementId === props.element?.id && editor.value) {
        // Use nextTick to ensure the DOM is updated and editor is ready
        nextTick(() => {
          // Directly set the editor instance in the store
          // This ensures the toolbar shows even if focus doesn't fire immediately
          store.updateEditorInstance(editor.value as unknown as typeof Editor)
          // Only focus if the editor is already in the viewport to prevent scrolling
          // This prevents scrolling to the last page when double-clicking on a text block
          setTimeout(() => {
            const editorDOM = editor.value?.view?.dom as HTMLElement
            if (editorDOM && isElementInViewport(editorDOM)) {
              editor.value?.commands.focus()
            }
          }, 10)
        })
      }
    },
    { immediate: false }
  )

  // Add watcher for link state changes
  watch(currentLinkState, newState => {
    if (newState?.href) {
      // Emit an event that the modal component can listen to
      editor.value?.emit('link-state-update', newState)
    }
  })

  onMounted(() => {
    const ed = editor.value
    if (!ed) return
    ed.on('focus', onRichTextEditorFocus)
    ed.on('selectionUpdate', onRichTextEditorSelectionUpdate)
    ed.on('blur', onRichTextEditorBlur)
    ed.on('create', onRichTextEditorCreate)
    // Force-sync the editor's content from the prop after mount. The editor was instantiated
    // synchronously in setup() with `content: props.element?.component.options.text`, but for
    // a freshly-mounted editor on a NEWLY ADDED page the `props.element` reactive read at
    // setup time can race against the `syncHeaderFooterToAllPages` clone assignment that
    // happens in the SAME synchronous tick as the page splice — leaving the tiptap instance
    // with empty / stale content even though the prop ultimately resolves to the correct
    // cloned text. By onMounted, props are fully settled and the DOM is committed, so we
    // unconditionally push the prop's text into the editor when it diverges from `getHTML()`.
    // For existing editors that were already in sync this is a no-op (content matches);
    // for the new-page editor this fixes the visibly-empty text block.
    nextTick(() => {
      const propText = props.element?.component?.options?.text
      if (propText != null && ed.getHTML() !== propText) {
        ed.commands.setContent(propText, false, { preserveWhitespace: 'full' })
      }
    })
    onUnmounted(() => {
      ed.off('focus', onRichTextEditorFocus)
      ed.off('selectionUpdate', onRichTextEditorSelectionUpdate)
      ed.off('blur', onRichTextEditorBlur)
      ed.off('create', onRichTextEditorCreate)
      ed.destroy()
    })
  })
</script>

<template>
  <UIRichTextEditor :editor="editor" />
</template>
